import { Card, Grid, Icon, IconButton, Tooltip, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { post, postNoResponse } from "utils/WebService";
import RegrasFilaCreateEdit from "./RegrasFilaCreateEdit";
import MDSnackbar from "components/MDSnackbar";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

export default function RegrasFilaConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [filters, setFilters] = useState({
        nome: '',
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };
        tableState.current = updatedTableState;

        // Simulando filtro para não quebrar (pois a API atual list-all traz tudo)
        // O ideal é a API ter paginação, mas vamos filtrar no front por enquanto.
        import("utils/WebService").then(({ get }) => {
            get("/regra-fila/list-all").then((res) => {
                let list = res || [];
                const nomeFilter = updatedTableState.filtered.find(f => f.id === 'nome');
                if (nomeFilter && nomeFilter.value) {
                    list = list.filter(item => item.nome.toLowerCase().includes(nomeFilter.value.toLowerCase()));
                }
                setData(list);
                setRecordsTotal(list.length);
                setPages(1);
                setLoadingTable(false);
                setAplicar(false);
            }).catch(() => {
                setLoadingTable(false);
                setAplicar(false);
            });
        });
    }, []);

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);

    const backFunction = () => {
        setIsCreateEdit(false);
        setSelectedId(null);
        setAplicar(true);
        fetchData({ ...tableState.current });
    };

    const handleDeleteClick = (row) => {
        setItemToDelete(row);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!itemToDelete) return;
        postNoResponse(`/regra-fila/delete/${itemToDelete.id}`)
            .then(() => {
                setSbMessage("Regra excluída com sucesso!");
                setSuccessSB(true);
                fetchData({ ...tableState });
            })
            .catch(() => {
                setSbMessage("Erro ao excluir regra. Verifique se existem vínculos.");
                setErrorSB(true);
            })
            .finally(() => {
                setDeleteDialogOpen(false);
                setItemToDelete(null);
            });
    };

    const handleApplyFilter = () => {
        let filtered = [];
        if (filters.nome) {
            filtered.push({ id: 'nome', value: filters.nome });
        }
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered });
    };

    const handleCleanFilter = () => {
        setFilters({ nome: '' });
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [] });
    };

    const columns = [
        {
            header: "ID",
            accessorKey: "id",
            cell: info => <DefaultCell>{info.getValue()}</DefaultCell>,
        },
        {
            header: "Nome da Regra",
            accessorKey: "nome",
            cell: info => <DefaultCell>{info.getValue()}</DefaultCell>,
        },
        {
            header: "Status",
            accessorKey: "ativo",
            cell: info => (
                <DefaultCell>
                    {info.getValue() ? "Ativo" : "Inativo"}
                </DefaultCell>
            ),
        },
        {
            header: "Ações",
            isSorted: false,
            cell: info => (
                <MDBox display="flex">
                    <Tooltip title="Editar">
                        <IconButton
                            onClick={() => {
                                setSelectedId(info.row.original.id);
                                setIsCreateEdit(true);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                        <IconButton onClick={() => handleDeleteClick(info.row.original)}>
                            <MDTypography variant="h4">
                                <Icon color="error" sx={{ marginRight: 1 }}>delete</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                </MDBox>
            ),
            width: "15%"
        },
    ];

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <RegrasFilaCreateEdit recordId={selectedId} closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? "#23233a" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome da Regra</InputLabel>
                            <MDInput size="small" placeholder="Nome..." value={filters.nome} onChange={({ target }) => setFilters({ ...filters, nome: target.value })} onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }} fullWidth />
                        </Grid>
                        <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                            <MDBox>
                                <MDButton size="medium" variant="contained" color="info" onClick={() => { setSelectedId(null); setIsCreateEdit(true); }}>Adicionar</MDButton>
                            </MDBox>
                            <MDBox>
                                <MDButton size="medium" sx={{ marginRight: 2 }} variant="contained" color="info" onClick={handleCleanFilter}>Limpar filtro</MDButton>
                                <MDButton size="medium" variant="contained" color="info" onClick={handleApplyFilter}>Aplicar filtro</MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={columns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    changeFilter={aplicar}
                    tableState={tableState}
                />
            </Grid>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    Tem certeza que deseja excluir a regra <strong>{itemToDelete?.nome}</strong>?
                    <br/><br/>
                    <MDTypography variant="caption" color="error">
                        Atenção: Esta ação não poderá ser desfeita.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setDeleteDialogOpen(false)} color="secondary">
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmDelete} color="error" variant="contained">
                        Excluir
                    </MDButton>
                </DialogActions>
            </Dialog>

            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Erro"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />
        </Grid>
    );
}
